import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MoodChart from './MoodChart';
import AttachmentStyleChart from './AttachmentStyleChart';
import TopicCategoriesChart from './TopicCategoriesChart';
import TriggersChart from './TriggersChart';
import RootsChart from './RootsChart';
import {
  LIFE_ANALYSIS_SECTIONS,
  LifeAnalysisIcons,
  ATTACHMENT_OVERVIEW,
  TOPICS_OVERVIEW,
  TRIGGERS_OVERVIEW,
  ROOTS_OVERVIEW,
} from '../constants/lifeAnalysisSections';
import './LifeAnalysisPage.css';

const TAB_IDS = ['mood', 'topics', 'triggers', 'attachment', 'roots'];
const DEFAULT_TAB = 'mood';

function getSectionById(id) {
  return LIFE_ANALYSIS_SECTIONS.find((s) => s.id === id) || LIFE_ANALYSIS_SECTIONS[0];
}

function InfoPanelContent({ sectionId }) {
  const section = getSectionById(sectionId);
  if (section.infoTooltip) {
    return <p className="life-analysis-info-panel-text">{section.infoTooltip}</p>;
  }
  let overview = null;
  if (sectionId === 'attachment') overview = ATTACHMENT_OVERVIEW;
  else if (sectionId === 'topics') overview = TOPICS_OVERVIEW;
  else if (sectionId === 'triggers') overview = TRIGGERS_OVERVIEW;
  else if (sectionId === 'roots') overview = ROOTS_OVERVIEW;
  if (!overview) return <p className="life-analysis-info-panel-text">No info available.</p>;
  return (
    <div className="life-analysis-info-panel-overview">
      {overview.map((block, i) =>
        block.type === 'list' ? (
          <ul key={i} className="att-overview-list">
            {block.items.map((item, j) => (
              <li key={j} className="att-overview-list-item">{item}</li>
            ))}
          </ul>
        ) : (
          <span
            key={i}
            className={`att-overview-${block.type}${block.type === 'header' && block.text === 'Disclaimer' ? ' att-overview-header--disclaimer' : ''}`}
          >
            {block.text}
          </span>
        )
      )}
    </div>
  );
}

function LifeAnalysisPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const infoPanelRef = useRef(null);

  const tabParam = searchParams.get('tab');
  const activeSectionId = TAB_IDS.includes(tabParam) ? tabParam : DEFAULT_TAB;
  const expandTrackMood = searchParams.get('track') === '1' && activeSectionId === 'mood';
  const [infoOpen, setInfoOpen] = React.useState(false);

  useEffect(() => {
    if (!TAB_IDS.includes(tabParam)) {
      setSearchParams({ tab: DEFAULT_TAB }, { replace: true });
    }
  }, [tabParam, setSearchParams]);

  const setTab = (id) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  useEffect(() => {
    if (infoPanelRef.current) infoPanelRef.current.scrollTop = 0;
  }, [activeSectionId, infoOpen]);

  const publicUrl = process.env.PUBLIC_URL || '';

  return (
    <div className="life-analysis-page">
      <header className="life-analysis-header">
        <button type="button" className="life-analysis-back" onClick={() => navigate('/')} aria-label="Back to home">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
          </svg>
        </button>
        <button type="button" className="life-analysis-header-brand" onClick={() => navigate('/')} aria-label="3AM AI home">
          <img src={`${publicUrl}/logo.png`} alt="" className="life-analysis-header-logo" />
          <span className="life-analysis-header-title">3AM AI</span>
        </button>
        <div className="life-analysis-header-spacer" aria-hidden />
      </header>

      <nav className="life-analysis-tabs" aria-label="Chart sections">
        {TAB_IDS.map((id) => {
          const s = getSectionById(id);
          return (
            <button
              key={id}
              type="button"
              className={`life-analysis-tab ${id === activeSectionId ? 'life-analysis-tab--active' : ''}`}
              onClick={() => setTab(id)}
              aria-current={id === activeSectionId ? 'true' : undefined}
            >
              <span className="life-analysis-tab-icon">{LifeAnalysisIcons[id]}</span>
              {s.label}
            </button>
          );
        })}
      </nav>

      <main className="life-analysis-main">
        <section id={activeSectionId} className="life-analysis-section">
          <div className="life-analysis-placeholder">
            <div
              className={`life-analysis-info-wrap${infoOpen ? ' is-open' : ''}`}
              onMouseEnter={() => setInfoOpen(true)}
              onMouseLeave={() => setInfoOpen(false)}
            >
              <span className="life-analysis-info-btn" aria-label="Info">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </span>
              {infoOpen && (
                <div ref={infoPanelRef} className="life-analysis-info-panel" role="tooltip" aria-label="Chart info">
                  <InfoPanelContent sectionId={activeSectionId} />
                </div>
              )}
            </div>
              {activeSectionId === 'mood' && <MoodChart expandTrackMood={expandTrackMood} onExpandTrackMoodHandled={() => setSearchParams({ tab: 'mood' }, { replace: true })} />}
              {activeSectionId === 'attachment' && <AttachmentStyleChart />}
              {activeSectionId === 'topics' && <TopicCategoriesChart />}
              {activeSectionId === 'triggers' && <TriggersChart />}
              {activeSectionId === 'roots' && <RootsChart />}
          </div>
        </section>
      </main>
    </div>
  );
}

export default LifeAnalysisPage;
